import { channelDef } from "./inboxChannels";

/** @typedef {'action_required' | 'info' | 'default'} InboxEmptyVariant */

/** Remove embedded URLs from server hints (action links use action_url). */
export function stripHintUrls(text) {
  if (!text) return "";
  return text
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s*Enable it here:\s*/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function isChannelConfigured(channel, def, inboxSummary) {
  const configured = new Set(inboxSummary?.configured_channels || []);
  if (def?.connectionKind) {
    return configured.has(channel) || configured.has(def.connectionKind);
  }
  return configured.has(channel);
}

/**
 * Map channel + inbox summary to empty-state props.
 * @param {string} channel
 * @param {object | null} inboxSummary
 * @param {boolean} hasFilters
 */
export function channelEmptyStateModel(channel, inboxSummary, hasFilters) {
  if (hasFilters) {
    return {
      variant: "default",
      title: "No matches",
      description: "Try a different search or filter, or switch channel tabs.",
    };
  }

  const def = channelDef(channel);
  const channelLabel = def?.label || channel;
  const health = inboxSummary?.channel_health?.[channel];
  const channelTotal = inboxSummary?.channels?.[channel]?.total || 0;
  const isConfigured = isChannelConfigured(channel, def, inboxSummary);

  if (health?.health === "disconnected" || (!isConfigured && channelTotal > 0)) {
    return {
      variant: "info",
      title: `${channelLabel} is disconnected`,
      description:
        stripHintUrls(health?.hint)
        || (
          "Historical conversations remain available. "
          + "Reconnect under Settings → Connections to receive new messages."
        ),
      settingsPath: health?.settings_path || "/settings/connections",
      ChannelIcon: def?.icon,
    };
  }

  if (!health?.hint) {
    return {
      variant: "default",
      title: "No conversations yet",
      description: `Messages on ${channelLabel} will appear here after they are received or synced.`,
      ChannelIcon: def?.icon,
    };
  }

  let variant = /** @type {InboxEmptyVariant} */ ("default");

  if (health.health === "api_disabled" || health.health === "pairing_required" || health.health === "not_configured") {
    variant = "action_required";
  } else if (health.health === "profile_only") {
    variant = "info";
  }

  if (variant === "default" && !health.action_url && !health.settings_path) {
    return {
      variant: "default",
      title: "No conversations yet",
      description: `Messages on ${channelLabel} will appear here after they are received or synced.`,
      ChannelIcon: def?.icon,
    };
  }

  const description = stripHintUrls(health.hint);

  return {
    variant,
    title: variant === "action_required" ? `${channelLabel} needs attention` : "No conversations yet",
    description,
    actionUrl: health.action_url,
    actionLabel: health.action_label,
    settingsPath: health.settings_path,
    ChannelIcon: def?.icon,
  };
}
